import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import {
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import { cn } from '@/lib/shadcn/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/modules/ui/components/command/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Forms';

interface ComboboxOption {
  value: string | number;
  label: string;
}

interface FormComboboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  description?: string;
  options: ComboboxOption[];
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  className?: string;
  disableSort?: boolean;
}

export function FormCombobox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  description,
  options,
  disabled,
  required,
  autoFocus,
  className,
  disableSort = false,
}: FormComboboxProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const [open, setOpen] = useState(false);

  const sortedOptions = disableSort
    ? options
    : [...options].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
      );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedOption = options.find(
          (opt) => String(opt.value) === String(field.value),
        );

        return (
          <FormItem>
            <FormLabel required={required}>{label}</FormLabel>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    className={cn(
                      "border-input data-[placeholder]:text-input-placeholder [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                      !selectedOption && 'text-input-placeholder',
                      className,
                    )}
                  >
                    <span className="truncate text-left">
                      {selectedOption?.label ?? placeholder}
                    </span>
                    <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
                  </button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[--radix-popover-trigger-width] overflow-hidden p-0"
              >
                <Command>
                  <CommandInput placeholder={searchPlaceholder} />
                  <CommandList className="max-h-[360px]">
                    <CommandEmpty>{emptyMessage}</CommandEmpty>
                    <CommandGroup>
                      {sortedOptions.map((option) => (
                        <CommandItem
                          key={String(option.value)}
                          value={String(option.value)}
                          keywords={[option.label]}
                          onSelect={() => {
                            field.onChange(option.value);
                            setOpen(false);
                          }}
                        >
                          <CheckIcon
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              String(field.value) === String(option.value)
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          <span className="truncate">{option.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
